import { NullableMeasurementLike } from 'data/models/Measurement';
import { ConvertedMeasurement } from 'lib/ConvertedMeasurement';
import UOM from 'lib/UOM';
import { notNullish } from 'lib/notNullish';

import { CSVField, ExcelDateTimeFormat } from 'common/services/csvExport';

import { PierSettings } from 'data/models/PierSettings';
import { VesselSideDisplayName } from 'server/enums/BerthReservation';
import { LloydResult } from 'server/models/LloydResult';
import { Vessel } from 'data/models/Vessel';

import { PilotJobOrderViewModel } from './model';

export function CsvColumnNames(settings : PierSettings) {
    return {
        MovementType           : 'Type',
        MovementStartEst       : 'Start Date/Time (Est)',
        MovementStartActual    : 'Start Date/Time (Actual)',
        MovementEndEst         : 'End Date/Time (Est)',
        MovementEndActual      : 'End Date/Time (Act)',
        VesselName             : 'Vessel Name',
        From                   : 'From',
        To                     : 'To',
        FromTerminal           : 'From Term.',
        ToTerminal             : 'To Term.',
        AgentCode              : 'Agent Code',
        AgencyName             : 'Agency Name',
        AttendingAgent         : 'Attending Agent ',
        AttendingAgentEmail    : 'Attending Agent Email',
        GrossTonnage           : 'Gross Tonnage',
        MovementLengthFeet     : 'Length (feet)',
        MovementLengthMeters   : 'Length (meters)',
        MovementBeamFeet       : 'Beam  (feet)',
        MovementBeamMeters     : 'Beam  (meters)',
        MovementDeepDraftFeet  : 'Deep Draft (feet)',
        MovementDeepDraftMeters: 'Deep Draft (meters)',
        MovementAirDraftFeet   : 'Air Draft (feet)',
        MovementAirDraftMeters : 'Air Draft (meters)',
        TugCode                : 'Tug Code',
        SideTo                 : 'Side To',
        PilotsAssigned         : 'Pilots Assigned',
        BowThrusterPresent     : 'BT Present?',
        BowThrusterSize        : 'BT Size',
        BowThrusterUoM         : 'BT UoM',
        BowThrusterWorking     : 'BT Working?',
        VesselIMO              : 'IMO',
        MMSI                   : 'MMSI',
        VesselOfficialNumber   : 'Official Number',
        VesselCallSign         : 'CallSign',
        VesselVIN              : 'VIN',
        VesselOperatorLloyds   : 'Operator',
        VesselDeadweightLloyds : 'Deadweight',
        ShiptypeLevel4         : 'ShiptypeLevel4',
        VesselTEULloyds        : 'TEU',
        VesselYearOfBuildLloyds: 'YearOfBuild',
        Breadth                : 'Breadth', 
        Depth                  : 'Depth',
        Draught                : 'Draught',
        FlagCode               : 'Flag Code',
        FlagName               : 'Flag Name',
        KeelToMastHeight       : 'Keel To Mast Height', 
        PassengerCapacity      : 'PAX Capacity',
        ShipStatus             : 'Ship Status',
        TEU                    : 'TEU',
        ThrustersDescriptiveNarrative : 'Thrusters Desc.',
        LastPort               : 'LastPort',
        NextPort               : 'NextPort',
    };
}

export function CSVColumns(settings : PierSettings) : CSVField<PilotJobOrderViewModel>[] {
    const colNames = CsvColumnNames(settings);
    return [{
        name: colNames.MovementType,
        formatter: item => item.MovementTypeText()
    }, {
        name: colNames.MovementStartEst,
        formatter: item => ExcelDateTimeFormat(item.Model.Start.Estimated())
    }, {
        name: colNames.MovementStartActual,
        formatter: item => item.Model.Start.Actual() ? ExcelDateTimeFormat(item.Model.Start.Actual()!) : ''
    }, {
        name: colNames.MovementEndEst,
        formatter: item => ExcelDateTimeFormat(item.Model.End.Estimated())
    }, {
        name: colNames.MovementEndActual,
        formatter: item => item.Model.End.Actual() ? ExcelDateTimeFormat(item.Model.End.Actual()!) : ''
    }, {
        name: colNames.VesselName,
        formatter: item => item.VesselNameText()
    }, {
        name: colNames.From,
        formatter: item => {
            const pt = item.Model.EffectiveFromPoint(),
                l = pt && pt.Location();
            return l && l.Name() || '';
        }
    }, {
        name: colNames.To,
        formatter: item => {
            const pt = item.Model.EffectiveToPoint(),
                l = pt && pt.Location();
            return l && l.Name() || '';
        }
    }, {
        name: colNames.FromTerminal,
        formatter: item => {
            const pt = item.Model.EffectiveFromPoint(),
                l = pt && pt.Location();
                const location = l && l.Name()
                const terminal = location ? location.split(" ")[0] : '';
            return terminal;
        }
    },
    {
        name: colNames.ToTerminal,
        formatter: item => {
            const pt = item.Model.EffectiveToPoint(),
                l = pt && pt.Location();
                const location = l && l.Name();
                const terminal = location ? location.split(" ")[0] : '';
            return terminal;
        }
    }, {
        name: colNames.AgentCode,
        formatter: item => item.AgencyCodeText()
    }, {
        name: colNames.AgencyName,
        formatter: item => item.Model.EffectivePortCall() && item.Model.EffectivePortCall()!.Agent() && item.Model.EffectivePortCall()!.Agent()!.Organization() && item.Model.EffectivePortCall()!.Agent()!.Organization()!.Name() || ''
    }, {
        name: colNames.AttendingAgent,
        formatter: item => item.Model.EffectivePortCall() && item.Model.EffectivePortCall()!.Agent() && item.Model.EffectivePortCall()!.Agent()!.FullName() || ''
    }, {
        name: colNames.AttendingAgentEmail,
        formatter: item => item.Model.EffectivePortCall() && item.Model.EffectivePortCall()!.Agent() && item.Model.EffectivePortCall()!.Agent()!.User() && item.Model.EffectivePortCall()!.Agent()!.User()!.Email() || ''
    }, {
        name: colNames.GrossTonnage,
        formatter: item => item.GrossTonnageText()
    }, {
        name: colNames.MovementLengthFeet,
        formatter: item => convert(item.Model.EffectiveLength as NullableMeasurementLike, UOM.Foot, 2)
    }, {
        name: colNames.MovementLengthMeters,
        formatter: item => convert(item.Model.EffectiveLength as NullableMeasurementLike, UOM.Meter, 2)
    }, {
        name: colNames.MovementBeamFeet,
        formatter: item => item.Model.EffectiveVessel() ? convert(item.Model.EffectiveVessel()!.Beam, UOM.Foot, 2) : ''
    }, {
        name: colNames.MovementBeamMeters,
        formatter: item => item.Model.EffectiveVessel() ? convert(item.Model.EffectiveVessel()!.Beam, UOM.Meter, 2) : ''
    }, {
        name: colNames.MovementDeepDraftFeet,
        formatter: item => convert(item.Model.EffectiveDeepDraft, UOM.Foot, 2)
    }, {
        name: colNames.MovementDeepDraftMeters,
        formatter: item => convert(item.Model.EffectiveDeepDraft, UOM.Meter, 2)
    }, {
        name: colNames.MovementAirDraftFeet,
        formatter: item => convert(item.Model.EffectiveAirDraft, UOM.Foot, 2)
    }, {
        name: colNames.MovementAirDraftMeters,
        formatter: item => convert(item.Model.EffectiveAirDraft, UOM.Meter, 2)
    }, {
        name: colNames.TugCode,
        formatter: item => item.Model.TugCompany() && item.Model.TugCompany()!.Code() || ''
    }, {
        name: colNames.SideTo,
        formatter: item => {
            const to = item.Model.EffectiveToPoint(),
                toRes = to && to.BerthReservation(), 
                from = item.Model.EffectiveFromPoint(),
                fromRes = from && from.BerthReservation();
            return toRes ? VesselSideDisplayName[toRes.SideTo()] :
                fromRes ? VesselSideDisplayName[fromRes.SideTo()] :
                '';
        }
    }, {
        name: colNames.PilotsAssigned,
        formatter: item => 
            // create list of pilot first initial last name, like 'E. Sanders, B. Cisco'
            item.Model.Pilots()
                .map(p => p.Pilot()?.User() ?? null)
                .filter(notNullish)
                .map(u => u.AbbreviatedName())
                .join(', ')
    }, {
        name: colNames.BowThrusterPresent,
        formatter: vesselField(v => yesNoNull(v.BowThruster.Present()))
    }, {
        name: colNames.BowThrusterSize,
        formatter: vesselField(v => v.BowThruster.Size.Num() === null ? null : v.BowThruster.Size.Num()!.toString())
    }, {
        name: colNames.BowThrusterUoM,
        formatter: vesselField(v => v.BowThruster.Size.UOM())
    }, {
        name: colNames.BowThrusterWorking,
        formatter: item => yesNoNull(item.Model.BowThrusterOperational())
    }, {
        name: colNames.VesselIMO,
        formatter: vesselField(v => v.IMO())
    }, {
        name: colNames.MMSI,
        formatter: lloydsResultField(l => l.MMSI())
    }, {
        name: colNames.VesselOfficialNumber,
        formatter: lloydsResultField(l => l.OfficialNumber())
    }, {
        name: colNames.VesselCallSign,
        formatter: vesselField(v => v.CallSign())
    }, {
        name: colNames.VesselVIN,
        formatter: vesselField(v => v.VIN())
    }, {
        name: colNames.VesselOperatorLloyds,
        formatter: lloydsResultField(r => r.Operator())
    }, {
        name: colNames.VesselDeadweightLloyds,
        formatter: lloydsResultField(r => r.Deadweight())
    }, {
        name: colNames.ShiptypeLevel4,
        formatter: lloydsResultField(l => l.ShiptypeLevel4())
    }, {
        name: colNames.VesselTEULloyds,
        formatter: lloydsResultField(l => l.TEU())
    }, {
        name: colNames.VesselYearOfBuildLloyds,
        formatter: lloydsResultField(l => l.YearOfBuild())
    }, {
        name: colNames.Breadth,
        formatter: lloydsResultField(l => l.Breadth())
    }, {
        name: colNames.Depth,
        formatter: lloydsResultField(l => l.Depth())
    }, {
        name: colNames.Draught,
        formatter: lloydsResultField(l => l.Draught())
    }, {
        name: colNames.FlagCode,
        formatter: lloydsResultField(l => l.FlagCode())
    }, {
        name: colNames.FlagName,
        formatter: lloydsResultField(l => l.FlagName())
    }, {
        name: colNames.KeelToMastHeight,
        formatter: lloydsResultField(l => l.KeelToMastHeight())
    }, {
        name: colNames.PassengerCapacity,
        formatter: lloydsResultField(l => l.PassengerCapacity())
    }, {
        name: colNames.ShipStatus,
        formatter: lloydsResultField(l => l.ShipStatus())
    }, {
        name: colNames.TEU,
        formatter: lloydsResultField(l => l.TEU())
    }, {   
        name: colNames.ThrustersDescriptiveNarrative,
        formatter: lloydsResultField(l => l.ThrustersDescriptiveNarrative())
    }, {
        name: colNames.LastPort,
        formatter: item => item.Model.EffectivePortCall() &&
                            item.Model.EffectivePortCall()!.LastPortOfCall() || ''
    }, {
        name: colNames.NextPort,
        formatter: item => item.Model.EffectivePortCall() &&
                            item.Model.EffectivePortCall()!.NextPortOfCall() || ''
    }
];
}

function vesselField(prop : (item : Vessel) => string | null) {
    return (item : PilotJobOrderViewModel) => {
        const v = item.Model.EffectiveVessel();
        return v && prop(v) || '';
    }
}

function lloydsResultField(prop : (item : LloydResult) => string | null) {
    return (item : PilotJobOrderViewModel) => {
        const v = item.Model.EffectiveVessel(),
            l = v && v.LloydServiceVessel(),
            r = l && l.VesselResult();
        return r && prop(r) || '';
    }
}

function yesNoNull(v : boolean | null) {
    return v === true ? 'Yes' : v === false ? 'No' : '';
}

function convert(v : NullableMeasurementLike, uom : string, precision : number) {
    return v.Num() === null ? '' : new ConvertedMeasurement(v, uom, precision).toString({ units: false })
}



// WEBPACK FOOTER //
// ./apps/pilots/dispatchdashboard/csvExport.ts